import React, { useState } from "react";
import FAQPage from "./FAQPage";
import IcingService from "./IcingService";
import SellYourKicks from "./SellYourKicks";
import { getTransformedCloudinaryUrl } from "../utils/cloudinary";

const PageContent = ({
  activePage,
  products,
  services = [],
  getImagesArray,
  handleProductClick,
  loading = false, // <-- add loading prop with default
}) => {
  const [modalOpen] = useState(false);

  // Preload the first 6 product images (or as many as exist)
  React.useEffect(() => {
    // Only preload optimized images that will be shown immediately (first 6 visible products)
    const links = [];
    products
      .filter((product) => product.stock > 0)
      .slice(0, 6)
      .forEach((product) => {
        const img = getImagesArray(product)[0];
        if (img) {
          const link = document.createElement("link");
          link.rel = "preload";
          link.as = "image";
          link.href = getTransformedCloudinaryUrl(img); // Preload only the optimized image
          document.head.appendChild(link);
          links.push(link);
        }
      });
    return () => {
      links.forEach((l) => {
        try {
          document.head.removeChild(l);
        } catch {}
      });
    };
  }, [products, getImagesArray]);

  switch (activePage) {
    case "home":
      if (loading) {
        return (
          <div className="flex justify-center items-center min-h-[300px]">
            <span className="text-4xl font-extrabold text-gray-400">
              Loading Products...
            </span>
          </div>
        );
      }
      return (
        <>
          <div className={modalOpen ? "hidden" : ""}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 2xl:grid-cols-6 gap-8">
              {products
                .filter((product) => product.stock > 0)
                .map((product) => {
                  const imagesArray = getImagesArray(product);
                  return (
                    <div
                      key={product.id}
                      className="product-card cursor-pointer transition duration-200 bg-white flex flex-col rounded-2xl overflow-hidden w-full h-[400px] border-4 border-gray-100 hover:border-indigo-600"
                      onClick={() => handleProductClick(product)}
                    >
                      <div className="w-full h-[220px] bg-white flex items-center justify-center">
                        <img
                          src={getTransformedCloudinaryUrl(imagesArray[0])}
                          alt={product.title || product.name}
                          className="w-full h-full object-cover"
                          loading="eager" // <-- add this
                        />
                      </div>
                      <div className="px-4 py-3 flex-1 flex flex-col justify-between">
                        <h3 className="text-xl font-extrabold text-gray-900 mb-2">
                          {product.title || product.name}
                        </h3>
                        <p className="text-base text-gray-700 mb-4">
                          {product.description}
                        </p>
                        <div className="flex justify-end">
                          <span className="text-xl font-bold text-indigo-600">
                            ${(product.price / 100).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </>
      );
    case "reice":
      return (
        <IcingService
          services={services}
          handleProductClick={handleProductClick}
        />
      );
    case "contact":
      return (
        <div className={modalOpen ? "hidden" : ""}>
          <div className="text-center p-8 bg-white rounded-3xl shadow-xl">
            <h1 className="text-4xl sm:text-5xl font-extrabold text-center text-gray-900 mb-4 leading-tight">
              Contact Us
            </h1>
            <p className="text-lg text-gray-700 mb-6">
              We'd love to hear from you! Please fill out the form below and we
              will get back to you as soon as possible.
            </p>
            <form
              className="space-y-4 max-w-sm mx-auto"
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.target;
                const name = form.elements["name"].value;
                const email = form.elements["email"].value;
                const subject = form.elements["subject"].value;
                const message = form.elements["message"].value;

                const res = await fetch("/.netlify/functions/contact-form", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ name, email, subject, message }),
                });
                const data = await res.json();
                if (data.success) {
                  alert("Message sent!");
                  form.reset();
                } else {
                  alert("Failed to send message.");
                }
              }}
            >
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
              <input
                type="email"
                name="email"
                placeholder="Your Email"
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
              <input
                type="text"
                name="subject"
                placeholder="Subject"
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
              <textarea
                name="message"
                placeholder="Your Message"
                rows="4"
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
              <button
                type="submit"
                className="w-full py-3 bg-indigo-600 text-white rounded-xl font-bold text-lg mt-2"
              >
                Send
              </button>
            </form>
          </div>
        </div>
      );
    case "about":
      return (
        <div className={modalOpen ? "hidden" : ""}>
          <div className="text-center p-8 bg-white rounded-3xl shadow-xl">
            <h1 className="text-4xl sm:text-5xl font-extrabold text-center text-gray-900 mb-4 leading-tight">
              About Our Store
            </h1>
            <p className="text-lg text-gray-700 mb-6">
              Our passion is to provide sneaker enthusiasts with a platform to
              buy, sell, and restore their favorite footwear. We believe every
              pair of shoes has a story, and we are dedicated to helping you
              keep that story alive through our services. Our team of experts is
              committed to quality, authenticity, and customer satisfaction. We
              strive to be the go-to destination for all your sneaker needs,
              from finding that perfect pair to bringing a classic back to its
              former glory.
            </p>
          </div>
        </div>
      );
    case "faq":
      return (
        <div className={modalOpen ? "hidden" : ""}>
          <FAQPage />
        </div>
      );
    case "sellyourkicks":
      return <SellYourKicks />;
    default:
      return null;
  }
};

export default PageContent;
